import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InformationStepsCard from "../../../components/InformationStepsCard";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import { styles as riskStyles } from "../../Risks/RiskManagement/styles/RiskManagement.styles";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../Organization/CodeSecurity/codeSecurityShared";
import { upsertItem, useTrustCenterAdmin } from "../hooks/useTrustCenterAdmin";
import { startDownload } from "../../../apis/trust-center-apis/trustCenterApis";
import RefreshButton, {
  TrustCenterRefreshContext,
} from "../components/RefreshButton";
import { siteSlug } from "../data/adminView";
import ResourcesTab from "./tabs/ResourcesTab";
import ComplianceTab, { NEW_REPORTS_CATEGORY } from "./tabs/ComplianceTab";
import UpdatesTab from "./tabs/UpdatesTab";
import ControlsTab from "./tabs/ControlsTab";
import { visibleControls } from "../data/controls";
import LogsTab from "./tabs/LogsTab";
import AccessRequestsTab from "./tabs/AccessRequestsTab";
import TrustCenterSettingsDialog from "./settings/TrustCenterSettingsDialog";
import PublishDialog from "./components/PublishDialog";
import {
  customUrl,
  describeChanges,
  previewUrl,
  regoditUrl,
} from "../data/publishing";
import OverviewTab from "./overview/OverviewTab";
import SimpleListTab, { FAQ_CONFIG } from "./tabs/SimpleListTab";
import SubprocessorsTab from "./tabs/SubprocessorsTab";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "compliance", label: "Compliance" },
  { value: "resources", label: "Resources" },
  { value: "controls", label: "Controls" },
  { value: "subprocessors", label: "Subprocessors" },
  { value: "faq", label: "FAQ" },
  { value: "updates", label: "Updates" },
  { value: "access-requests", label: "Access requests" },
  { value: "activity", label: "Logs" },
];

// The small "Copy link" / "Open page" buttons beside the page link.
const linkButtonSx = (theme) => ({
  textTransform: "none",
  fontWeight: 500,
  fontSize: 13,
  px: 1,
  py: 0.25,
  minWidth: 0,
  borderRadius: "6px",
  color: theme.customColors.text.secondary,
  border: `1px solid ${theme.customColors.border.main}`,
  "&:hover": {
    color: theme.customColors.text.strong,
    backgroundColor: theme.customColors.background.light,
  },
});

const shortDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

// Compact status bar: is it live, where, are there unpublished changes, and
// the actions. Three states:
//   Not published            Preview · Publish
//   Published                Preview · Publish changes (off) · Unpublish
//   Published + changes      Preview · Publish changes · Unpublish
// Edits save as a draft; buyers see them only after Publish changes.
// `saving`: a save is on its way to the server. Until it lands the card
// cannot know what is unpublished, so it says it is saving and holds
// Publish.
const PublishCard = ({
  isPublished,
  draft,
  saving,
  publishing,
  publicUrl,
  previewLink,
  onPublish,
  onUnpublish,
  onCopied,
  onOpenSettings,
}) => {
  const theme = useTheme();
  const onCustomDomain =
    isPublished && publishing.target === "custom" && !!publishing.customDomain;
  const liveUrl = onCustomDomain
    ? customUrl(publishing.customDomain)
    : publicUrl;
  const waitingForDns =
    onCustomDomain && publishing.customDomainStatus !== "active";
  const pending = isPublished && !!draft?.hasUnpublishedChanges;
  const changeLines = describeChanges(draft?.changes);
  const warning = (text) => (
    <Box
      component="span"
      sx={{ color: theme.customColors.status.warning.text }}
    >
      {text}
    </Box>
  );

  // The line under the bar: what the admin should know right now.
  const note = saving
    ? "Saving your changes…"
    : !publicUrl
      ? warning(
          "Add your company website or domain in Company Settings to get a link."
        )
      : waitingForDns
        ? warning(
            `Add a CNAME record for ${publishing.customDomain} pointing to ${publishing.cnameTarget}. Until it is found, your page is live at ${publicUrl}.`
          )
        : pending
          ? "Your edits are saved as a draft. Buyers see the last published version until you publish them. Use Preview to check them first."
          : isPublished
            ? `Live and up to date${
                draft?.contentPublishedAt
                  ? ` · published ${shortDate(draft.contentPublishedAt)}${
                      draft.contentPublishedBy
                        ? ` by ${draft.contentPublishedBy}`
                        : ""
                    }`
                  : ""
              }. Controls and laptop numbers update on their own.`
            : "Only you can see it until you publish. Use Preview to check it first.";

  return (
    <Paper
      elevation={0}
      sx={{
        px: 2.5,
        py: 2,
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            useFlexGap
            sx={{ flexWrap: "wrap", rowGap: 1 }}
          >
            <Chip
              size="small"
              label={isPublished ? "Published" : "Not published"}
              sx={{
                fontWeight: 600,
                color: isPublished
                  ? theme.customColors.status.success.text
                  : theme.customColors.text.secondary,
                backgroundColor: isPublished
                  ? theme.customColors.status.success.bg
                  : theme.customColors.background.light,
              }}
            />
            {saving && (
              <Chip
                size="small"
                icon={<CircularProgress size={12} thickness={5} />}
                label="Saving…"
                aria-live="polite"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.text.secondary,
                  backgroundColor: theme.customColors.background.light,
                  "& .MuiChip-icon": { color: "inherit", ml: 0.75 },
                }}
              />
            )}
            {pending && !saving && (
              <Chip
                size="small"
                label="Unpublished changes"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.status.warning.text,
                  backgroundColor: theme.customColors.status.warning.bg,
                }}
              />
            )}
            {publicUrl && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                useFlexGap
                sx={{ flexWrap: "wrap", rowGap: 0.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.customColors.text.strong,
                    wordBreak: "break-all",
                    mr: 0.5,
                  }}
                >
                  {liveUrl}
                </Typography>
                {/* Small labelled buttons, not bare icons. */}
                <Button
                  size="small"
                  aria-label="Copy link"
                  startIcon={
                    <ContentCopyIcon sx={{ fontSize: "15px !important" }} />
                  }
                  onClick={() =>
                    navigator.clipboard?.writeText(liveUrl).then(onCopied)
                  }
                  sx={linkButtonSx(theme)}
                >
                  Copy link
                </Button>
                {isPublished && (
                  <Button
                    size="small"
                    aria-label="Open the live page"
                    startIcon={
                      <OpenInNewIcon sx={{ fontSize: "15px !important" }} />
                    }
                    href={liveUrl}
                    target="_blank"
                    rel="noopener"
                    sx={linkButtonSx(theme)}
                  >
                    Open page
                  </Button>
                )}
              </Stack>
            )}
            {waitingForDns && (
              <Chip
                size="small"
                label="Waiting for DNS"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.status.warning.text,
                  backgroundColor: theme.customColors.status.warning.bg,
                }}
              />
            )}
          </Stack>
          <Typography
            variant="body2"
            sx={{ mt: 0.75, color: theme.customColors.text.secondary }}
          >
            {note}
          </Typography>
          {pending && !saving && changeLines.length > 0 && (
            <Typography
              variant="caption"
              aria-label="Unpublished changes"
              sx={{
                display: "block",
                mt: 0.5,
                color: theme.customColors.text.secondary,
              }}
            >
              {changeLines.join(" · ")}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
            disabled={!publicUrl}
            // Preview shows the draft and signs the admin in, so it opens on
            // the app itself.
            href={previewLink || undefined}
            target="_blank"
            rel="noopener"
            sx={outlinedButtonSx(theme)}
          >
            Preview
          </Button>
          {isPublished ? (
            <Tooltip
              title={pending || saving ? "" : "Nothing new to publish"}
              arrow
              disableHoverListener={pending || saving}
            >
              <span>
                <Button
                  variant="contained"
                  disabled={!publicUrl || !pending || saving}
                  onClick={onPublish}
                  startIcon={
                    saving ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : undefined
                  }
                  sx={primaryButtonSx(theme)}
                >
                  {saving ? "Saving…" : "Publish changes"}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              disabled={!publicUrl || saving}
              onClick={onPublish}
              startIcon={
                saving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
              sx={primaryButtonSx(theme)}
            >
              {saving ? "Saving…" : "Publish"}
            </Button>
          )}
          {isPublished && (
            <Button
              variant="outlined"
              onClick={onUnpublish}
              sx={outlinedButtonSx(theme)}
            >
              Unpublish
            </Button>
          )}
          <Tooltip title="Settings" arrow>
            <IconButton
              aria-label="Trust Center settings"
              onClick={onOpenSettings}
              sx={{
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "8px",
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

const TrustCenterAdmin = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "overview";
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  // null when closed, otherwise the settings section to open on.
  const [settingsSection, setSettingsSection] = useState(null);
  const [saveError, setSaveError] = useState("");
  const {
    data,
    stored,
    loading,
    saving,
    error,
    update,
    publish,
    unpublish,
    refresh,
    download,
    connectDomain,
    checkDomain,
    removeDomain,
  } = useTrustCenterAdmin({ onError: setSaveError });
  const publicUrl = regoditUrl(siteSlug(data?.site));
  const previewLink = previewUrl(siteSlug(data?.site));
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);

  const notify = (message) =>
    setSnackbarInfo({
      open: true,
      message,
      severity: "success",
      key: Date.now(),
    });

  // Failing controls that visitors can actually see with the current setting.
  const failingShown = data
    ? visibleControls(
        data.controls,
        data.controlVisibility,
        data.frameworks
      ).filter((c) => c.status === "fail").length
    : 0;

  // After a save on a published page: remind the admin that buyers see the
  // change only once it is published (the server says what is pending).
  const [publishReminder, setPublishReminder] = useState(null);
  const remindToPublish = () => {
    const now = stored();
    if (now?.isPublished && now.draft?.hasUnpublishedChanges)
      setPublishReminder(Date.now());
  };

  // Every content edit goes through here: saves it as a draft, then reminds.
  const saveDraft = (changes) =>
    update(changes).then((ok) => {
      if (ok) remindToPublish();
      return ok;
    });

  // Runs a save and confirms it only once the server has it.
  const saveThen = (changes, message) =>
    saveDraft(changes).then((ok) => ok && notify(message));

  // Compliance → Upload report: saves the new resource(s), then attaches
  // them to the framework by the ids the server gave them. Makes the
  // "Reports" category first when the customer has none.
  const addReports = async (frameworkId, newResources) => {
    let reports = newResources;
    if (reports.some((r) => r.categoryId === NEW_REPORTS_CATEGORY.id)) {
      const madeCategory = await update((base) => ({
        resourceCategories: [
          ...base.resourceCategories,
          { name: NEW_REPORTS_CATEGORY.name, description: "" },
        ],
      }));
      const category = madeCategory
        ? stored()?.resourceCategories.find(
            (c) => c.name === NEW_REPORTS_CATEGORY.name
          )
        : null;
      if (!category) return;
      reports = reports.map((r) => ({ ...r, categoryId: category.id }));
    }
    const before = new Set((stored()?.resources || []).map((r) => r.id));
    const saved = await update((base) => ({
      resources: reports.reduce(
        (list, r) => upsertItem(list, r),
        base.resources
      ),
    }));
    if (!saved) return;
    const created = (stored()?.resources || []).filter(
      (r) => !before.has(r.id)
    );
    const attachable = created.filter((r) => r.visibility !== "private");
    if (attachable.length === 0) {
      setSaveError(
        "The report was added to Resources as Private, so it cannot be shown on a framework. Set it to Public or Requestable, then attach it."
      );
      return;
    }
    const framework = stored()?.frameworks.find((f) => f.id === frameworkId);
    const attached = await saveDraft((base) => ({
      frameworks: upsertItem(base.frameworks, {
        id: frameworkId,
        resourceIds: [
          ...(framework?.resourceIds || []),
          ...attachable.map((r) => r.id),
        ],
      }),
    }));
    if (attached)
      notify(
        `Report added to ${framework?.name || "the framework"}. Publish changes to show it to buyers.`
      );
  };

  if (!data) {
    return (
      <Box sx={riskStyles.pageContainer}>
        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={320} />
          </Stack>
        ) : (
          <Alert
            severity="error"
            sx={{ borderRadius: "12px", alignItems: "center" }}
            action={
              <RefreshButton label="Trust Center" onRefresh={() => refresh()} />
            }
          >
            {error?.graphQLErrors?.[0]?.message ||
              "Could not load your Trust Center. Click refresh to try again."}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <TrustCenterRefreshContext.Provider value={refresh}>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
      <Snackbar
        open={!!saveError}
        autoHideDuration={6000}
        onClose={(event, reason) => reason !== "clickaway" && setSaveError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSaveError("")}>
          {saveError}
        </Alert>
      </Snackbar>
      <PublishDialog
        open={publishOpen}
        data={data}
        regoditLink={publicUrl}
        previewLink={previewLink}
        onClose={() => setPublishOpen(false)}
        onPublish={(publishing) => {
          setPublishReminder(null);
          return publish(publishing).then(
            (ok) =>
              ok &&
              notify(
                data.isPublished
                  ? "Changes published. Buyers see them now."
                  : publishing.target === "custom"
                    ? `Published. Add the DNS record so ${publishing.customDomain} goes live.`
                    : "Trust Center published."
              )
          );
        }}
      />
      <Snackbar
        key={publishReminder || "reminder"}
        open={!!publishReminder}
        onClose={(event, reason) =>
          reason !== "clickaway" && setPublishReminder(null)
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          sx={{ alignItems: "center" }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                color="inherit"
                href={previewLink || undefined}
                target="_blank"
                rel="noopener"
              >
                Preview
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setPublishReminder(null);
                  setPublishOpen(true);
                }}
                sx={primaryButtonSx(theme)}
              >
                Publish changes
              </Button>
              <IconButton
                size="small"
                aria-label="Dismiss reminder"
                onClick={() => setPublishReminder(null)}
                sx={{ color: "inherit" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          Saved as a draft. Republish the page so buyers see this change.
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={unpublishOpen}
        onClose={() => setUnpublishOpen(false)}
        onConfirm={() => {
          setUnpublishOpen(false);
          unpublish().then((ok) => ok && notify("Trust Center unpublished."));
        }}
        heading="Unpublish your Trust Center?"
        content="Buyers can no longer open the page. Your content and link are kept, and you can publish again at any time."
        buttonText="Unpublish"
      />
      <TrustCenterSettingsDialog
        open={!!settingsSection}
        data={data}
        regoditLink={publicUrl}
        onConnectDomain={(domain) =>
          connectDomain(domain).then((ok) => {
            if (ok) notify(`${domain} saved. Add the DNS record below.`);
            return ok;
          })
        }
        onCheckDomain={checkDomain}
        onRemoveDomain={() =>
          removeDomain().then((ok) => {
            if (ok)
              notify("Custom domain removed. Your page uses the Regodit link.");
            return ok;
          })
        }
        onClose={() => setSettingsSection(null)}
      />
      <Box sx={riskStyles.pageContainer}>
        <Box sx={{ mb: 3 }}>
          <InformationStepsCard
            heading="**What should I do on this page?**"
            subtitle="Set up the page buyers see when they check your security, then share the link instead of emailing PDFs."
            steps={[
              "**Overview**: **Edit** sets your contact email, legal links and where data is stored; **Edit branding** sets the banner, colours, font and browser icon, with a live preview. Your company name, overview and website come from **Company Settings**.",
              "**Resources**: add categories, then add documents from Regodit, new uploads or links. Set each document to **Public**, **Requestable** (listed with a lock; visitors ask, you approve), or **Private**. Use Requestable for SOC 2 reports and pen test summaries. Drag resources to set their order; the first 4 per category show on your Overview.",
              "**Access requests**: a visitor picks documents and confirms their email with a code, then the request shows here. **Accept** emails them those documents. **Decline** sends nothing. Approval is per document: accepting one does not open the others.",
              "**Subprocessors** and **FAQ**: list the vendors that touch customer data and answer common questions.",
              "Click the **gear** for a **custom domain**, then **Preview**, **Publish**, and share the link.",
              "**Changes are drafts**: after the first publish, every edit (a new resource, a subprocessor switched on, an FAQ) is saved but not shown to buyers. Click **Preview** to check it, then **Publish changes** to put it live.",
              "**Controls**: status comes straight from Regodit and updates on its own. Click **Visibility settings** to choose which controls visitors see: only passing ones, all with status, or all without status.",
              "**Logs**: what happened on your Trust Center: page visits, opened files, access requests and approvals, and every published change, with who did it.",
            ]}
            collapsible={true}
            defaultExpanded={false}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <PublishCard
            isPublished={data.isPublished}
            draft={data.draft}
            saving={saving}
            publishing={data.publishing}
            onOpenSettings={() => setSettingsSection("domain")}
            publicUrl={publicUrl}
            previewLink={previewLink}
            onCopied={() => notify("Link copied.")}
            onPublish={() => setPublishOpen(true)}
            onUnpublish={() => setUnpublishOpen(true)}
          />
        </Box>

        {/* Shown where it is acted on, not above every tab. */}
        {failingShown > 0 && (tab === "overview" || tab === "controls") && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: "12px" }}>
            {failingShown} {failingShown === 1 ? "control is" : "controls are"}{" "}
            failing, and buyers can see this on your Trust Center. Fix them on
            the Controls page, or change what is shown on the Controls tab.
          </Alert>
        )}

        <TabContext value={tab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: theme.customColors.border.main,
              mb: 2,
            }}
          >
            <TabList
              onChange={(_, value) =>
                setSearchParams({ tab: value }, { replace: true })
              }
              variant="scrollable"
              allowScrollButtonsMobile
              aria-label="Trust Center tabs"
            >
              {TABS.map((t) => (
                <Tab
                  key={t.value}
                  label={t.label}
                  value={t.value}
                  sx={{ minWidth: 0, px: 2 }}
                />
              ))}
            </TabList>
          </Box>
          <TabPanel value="overview" sx={{ p: 0 }}>
            <OverviewTab
              data={data}
              onSave={(changes) => saveThen(changes, "Overview saved.")}
            />
          </TabPanel>
          <TabPanel value="compliance" sx={{ p: 0 }}>
            <ComplianceTab
              frameworks={data.frameworks}
              resources={data.resources}
              categories={data.resourceCategories}
              onChange={saveDraft}
              onOpenResource={(id) =>
                setSearchParams({ tab: "resources", resource: String(id) })
              }
              onAddReports={addReports}
            />
          </TabPanel>
          <TabPanel value="updates" sx={{ p: 0 }}>
            <UpdatesTab
              updates={data.updates || []}
              subscriberCount={data.updateSubscriberCount}
              onChange={saveDraft}
              onNotify={notify}
            />
          </TabPanel>
          <TabPanel value="resources" sx={{ p: 0 }}>
            <ResourcesTab
              categories={data.resourceCategories}
              resources={data.resources}
              focusResourceId={Number(searchParams.get("resource")) || null}
              pageUrl={publicUrl}
              onChange={saveDraft}
              onCopied={() => notify("Link copied.")}
              onDownload={(resource) =>
                download(resource.id)
                  .then((link) => startDownload(link.url))
                  .catch((err) => setSaveError(err.message))
              }
            />
          </TabPanel>
          <TabPanel value="controls" sx={{ p: 0 }}>
            <ControlsTab
              controls={data.controls}
              frameworks={data.frameworks}
              visibility={data.controlVisibility}
              onChange={(changes) =>
                saveThen(changes, "Control visibility saved.")
              }
            />
          </TabPanel>
          <TabPanel value="subprocessors" sx={{ p: 0 }}>
            <SubprocessorsTab
              subprocessors={data.subprocessors}
              integrationSubprocessors={data.integrationSubprocessors || {}}
              onChange={saveDraft}
            />
          </TabPanel>
          <TabPanel value="faq" sx={{ p: 0 }}>
            <SimpleListTab
              config={FAQ_CONFIG}
              items={data.faqs}
              onChange={(faqs) => saveDraft({ faqs })}
            />
          </TabPanel>
          <TabPanel value="access-requests" sx={{ p: 0 }}>
            <AccessRequestsTab
              alertEmails={data.requestAlertEmails || []}
              // A setting, not page content: saved without a republish.
              onSaveAlerts={(emails) =>
                update({ requestAlertEmails: emails }).then((ok) => {
                  if (ok)
                    notify(
                      emails.length
                        ? "Email alerts saved."
                        : "Email alerts turned off."
                    );
                  return ok;
                })
              }
            />
          </TabPanel>
          <TabPanel value="activity" sx={{ p: 0 }}>
            <LogsTab />
          </TabPanel>
        </TabContext>
      </Box>
    </TrustCenterRefreshContext.Provider>
  );
};

export default TrustCenterAdmin;
